import { createContext, useContext, useEffect, useState, useRef, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User as SupabaseUser, Session } from '@supabase/supabase-js';
import { supabase } from '@/config/supabase';
import { ROUTES } from '@/config/routes';
import type { User as UserProfile } from '@/types/entities';

/**
 * AuthContext - Single Source of Truth Architecture
 *
 * ARCHITECTURAL PRINCIPLE:
 * AuthContext owns BOTH auth readiness AND resolved profile data in ONE unified state.
 * This eliminates timing gaps between readiness flag and profile availability.
 *
 * AUTH RESOLUTION STATES:
 * - 'loading': Initial app load or post-login profile fetch in progress (routing blocked)
 * - 'authenticated': User authenticated + profile resolved successfully (routing ready)
 * - 'authenticated_no_profile': User authenticated but profile missing/error (routing ready, controlled error state)
 * - 'unauthenticated': No user session (routing ready)
 *
 * ROUTING READINESS:
 * - isAuthReady = true when state is NOT 'loading'
 * - Guards MUST wait for isAuthReady before making routing decisions
 * - When ready, guards check BOTH authStatus AND resolvedProfile from SAME source
 * - No split between "readiness flag" and "profile data" - they update atomically
 *
 * CRITICAL FIX:
 * Previous architecture had split source of truth:
 * - profileResolutionState (AuthContext state) controlled readiness
 * - userProfile (useProfileFetching hook state) provided data
 * - These updated on different timing, creating gaps where routing unlocked with null profile
 *
 * New architecture:
 * - resolveAuthenticatedUser() is THE ONLY function that fetches profile
 * - Used in BOTH bootstrap (INITIAL_SESSION) and login (SIGNED_IN) paths
 * - Sets authStatus + resolvedProfile in ONE atomic state update
 * - No logic drift between login and refresh flows
 */
type AuthStatus = 'loading' | 'authenticated' | 'authenticated_no_profile' | 'unauthenticated';

interface AuthContextType {
  user: SupabaseUser | null;
  userProfile: UserProfile | null;
  session: Session | null;
  /** Deprecated: Use isAuthReady instead. Kept for backward compatibility. */
  loading: boolean;
  /** Deprecated: Use isAuthReady instead. Kept for backward compatibility. */
  authBootstrapping: boolean;
  /** Deprecated: No longer used. Kept for backward compatibility. */
  profileLoading: boolean;
  /** True when auth system is ready for routing decisions (authStatus !== 'loading') */
  isAuthReady: boolean;
  /** Current auth resolution state - unified state machine */
  profileResolutionState: 'bootstrapping' | 'resolving' | 'resolved'; // Deprecated, mapped from authStatus
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);

  // UNIFIED AUTH STATE - Single source of truth for BOTH readiness AND profile data
  const [authStatus, setAuthStatus] = useState<AuthStatus>('loading');
  const [resolvedProfile, setResolvedProfile] = useState<UserProfile | null>(null);

  const navigate = useNavigate();

  // Bootstrap gate: Track when initial bootstrap is complete
  const isBootstrapComplete = useRef<boolean>(false);

  // Session refresh tracking: Distinguish initial auth from background session refresh
  const isInitialAuth = useRef<boolean>(true);

  // Profile cache timestamp for staleness detection
  const lastProfileFetchRef = useRef<number>(0);
  const PROFILE_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  // CRITICAL FIX: Deferred profile resolution trigger
  // Calling async Supabase operations INSIDE onAuthStateChange callback causes deadlock
  // This state triggers profile resolution OUTSIDE the callback via useEffect
  const [pendingAuthResolution, setPendingAuthResolution] = useState<SupabaseUser | null>(null);

  /**
   * Single source of truth for routing readiness
   *
   * isAuthReady = true when authStatus is NOT 'loading'
   *
   * When false ('loading'):
   *   - Guards MUST show loading spinner
   *   - Guards MUST NOT make routing decisions
   *
   * When true (any other state):
   *   - Guards can safely check resolvedProfile and onboarding_completed_at
   *   - 'authenticated' + resolvedProfile !== null: profile loaded successfully
   *   - 'authenticated_no_profile': profile failed to load (controlled error state)
   *   - 'unauthenticated': no user session
   *
   * CRITICAL: resolvedProfile comes from SAME source as authStatus
   * They update atomically in resolveAuthenticatedUser()
   */
  const isAuthReady = authStatus !== 'loading';

  /**
   * SINGLE UNIFIED PROFILE RESOLUTION FUNCTION
   *
   * This is THE ONLY function that fetches user profile and sets auth state.
   * Used in BOTH:
   * - Bootstrap path (initial app load with existing session)
   * - Login path (SIGNED_IN event after authentication)
   *
   * ATOMIC STATE UPDATE:
   * Sets authStatus + resolvedProfile together in ONE setState batch
   * This eliminates timing gaps where routing unlocks with null profile
   *
   * @param sessionUser - Supabase auth user from session
   * @returns Resolved profile or null
   */
  const resolveAuthenticatedUser = async (sessionUser: SupabaseUser): Promise<UserProfile | null> => {
    console.log('[AuthContext] resolveAuthenticatedUser - START', { userId: sessionUser.id });

    try {
      // Create AbortController for hard timeout
      const abortController = new AbortController();
      const timeoutId = setTimeout(() => {
        console.log('[AuthContext] Profile fetch hard timeout after 10 seconds');
        abortController.abort();
      }, 10000);

      const queryStart = performance.now();

      // Fetch profile from database
      const { data: profile, error } = await supabase
        .from('users')
        .select('id, first_name, last_name, email, onboarding_completed_at, created_at, updated_at')
        .eq('id', sessionUser.id)
        .abortSignal(abortController.signal)
        .maybeSingle<UserProfile>();

      clearTimeout(timeoutId);

      const queryTime = performance.now() - queryStart;
      console.log(`[AuthContext] Profile fetch completed in ${queryTime.toFixed(2)}ms`, {
        hasProfile: !!profile,
        error: error?.message,
      });

      if (error) {
        console.error('[AuthContext] Profile fetch error:', error);
        // ATOMIC UPDATE: Set status + null profile together
        setAuthStatus('authenticated_no_profile');
        setResolvedProfile(null);
        lastProfileFetchRef.current = Date.now();
        return null;
      }

      if (!profile) {
        console.warn('[AuthContext] No profile found for authenticated user');
        // ATOMIC UPDATE: Set status + null profile together
        setAuthStatus('authenticated_no_profile');
        setResolvedProfile(null);
        lastProfileFetchRef.current = Date.now();
        return null;
      }

      // Success - ATOMIC UPDATE: Set status + profile together
      console.log('[AuthContext] Profile resolved successfully', {
        hasOnboarding: !!profile.onboarding_completed_at,
      });
      setAuthStatus('authenticated');
      setResolvedProfile(profile);
      lastProfileFetchRef.current = Date.now();
      return profile;
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.error('[AuthContext] Profile fetch aborted due to timeout');
      } else {
        console.error('[AuthContext] Profile fetch exception:', error);
      }
      // ATOMIC UPDATE: Set status + null profile together
      setAuthStatus('authenticated_no_profile');
      setResolvedProfile(null);
      lastProfileFetchRef.current = Date.now();
      return null;
    }
  };

  // CRITICAL FIX: Deferred profile resolution useEffect
  // This runs OUTSIDE the onAuthStateChange callback context to avoid Supabase async deadlock
  useEffect(() => {
    if (!pendingAuthResolution) return;

    console.log('[AuthContext] ⚡ Deferred profile resolution triggered OUTSIDE callback');

    const resolveDeferredAuth = async () => {
      try {
        console.log('[AuthContext] Starting deferred profile resolution for user:', pendingAuthResolution.id);
        await resolveAuthenticatedUser(pendingAuthResolution);
        console.log('[AuthContext] ✅ Deferred profile resolution completed successfully');
      } catch (error) {
        console.error('[AuthContext] Deferred profile resolution failed:', error);
        // resolveAuthenticatedUser already sets error state
      } finally {
        // Clear the pending trigger
        setPendingAuthResolution(null);
      }
    };

    resolveDeferredAuth();
  }, [pendingAuthResolution]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let isMounted = true;

    // Get initial session - this is bootstrap phase
    const initSession = async () => {
      console.log('[AuthContext] ========== BOOTSTRAP STARTED ==========');
      console.log('[AuthContext] Auth status: loading');

      try {
        // Get session first
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        console.log('[AuthContext] Initial session result:', { hasSession: !!session, error: sessionError });

        if (!isMounted) {
          console.log('[AuthContext] Component unmounted, aborting bootstrap');
          return;
        }

        // Set session and user immediately
        setSession(session);
        setUser(session?.user ?? null);

        // Mark bootstrap as complete BEFORE starting profile fetch
        // This prevents onAuthStateChange from interfering
        isBootstrapComplete.current = true;

        // Now resolve profile if we have a session
        if (session?.user) {
          console.log('[AuthContext] Resolving authenticated user profile (blocks routing)');
          await resolveAuthenticatedUser(session.user);
        } else {
          console.log('[AuthContext] No session, setting unauthenticated state');
          setAuthStatus('unauthenticated');
          setResolvedProfile(null);
        }

        // Mark initial auth as complete - future SIGNED_IN events are session refreshes
        isInitialAuth.current = false;

        console.log('[AuthContext] ========== BOOTSTRAP FINISHED ==========');
      } catch (error) {
        console.error('[AuthContext] Fatal error in bootstrap:', error);
        if (isMounted) {
          // Set error state but unlock routing
          setAuthStatus('unauthenticated');
          setResolvedProfile(null);
          isInitialAuth.current = false;
          console.log('[AuthContext] ========== BOOTSTRAP FAILED ==========');
        }
      }
    };

    initSession();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      console.log('[AuthContext] Auth state changed:', _event, !!session);

      // CRITICAL: Ignore ALL auth events during bootstrap
      // This prevents interference with the initial session flow
      if (!isBootstrapComplete.current) {
        console.log('[AuthContext] ⚠️ IGNORING AUTH EVENT - BOOTSTRAP STILL IN PROGRESS:', _event);
        return;
      }

      console.log('[AuthContext] Processing auth event after bootstrap:', _event);

      // Skip INITIAL_SESSION as it's already handled by bootstrap
      if (_event === 'INITIAL_SESSION') {
        console.log('[AuthContext] Ignoring INITIAL_SESSION event (already handled by bootstrap)');
        return;
      }

      // Handle SIGNED_OUT event explicitly
      if (_event === 'SIGNED_OUT') {
        console.log('[AuthContext] User signed out, clearing all state');
        setSession(null);
        setUser(null);
        setAuthStatus('unauthenticated');
        setResolvedProfile(null);
        lastProfileFetchRef.current = 0;
        isInitialAuth.current = true; // Reset for next login
        return;
      }

      // Only process other events after bootstrap is complete
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        // Detect if this is a background session refresh (tab focus, token refresh, etc.)
        const isSessionRefresh = !isInitialAuth.current && resolvedProfile !== null;
        const timeSinceLastFetch = Date.now() - lastProfileFetchRef.current;
        const needsRefresh = timeSinceLastFetch > PROFILE_CACHE_DURATION;

        console.log('[AuthContext] Auth event analysis:', {
          event: _event,
          isSessionRefresh,
          hasProfile: !!resolvedProfile,
          isStale: needsRefresh,
          timeSinceLastFetch: `${(timeSinceLastFetch / 1000).toFixed(1)}s`,
        });

        if (isSessionRefresh) {
          // Background session refresh (tab focus, auto token refresh)
          console.log('[AuthContext] 🔄 Detected background session refresh');

          if (needsRefresh) {
            // Profile is stale - refresh silently without blocking UI
            console.log('[AuthContext] Profile is stale, refreshing silently in background');

            // Fetch profile silently - does not block routing
            resolveAuthenticatedUser(session.user)
              .then((profile) => {
                if (profile !== null) {
                  console.log('[AuthContext] Background profile refresh successful');
                } else {
                  console.warn('[AuthContext] Background profile refresh failed');
                }
              })
              .catch((error) => {
                console.error('[AuthContext] Background profile refresh error:', error);
              });
          } else {
            // Profile is fresh - skip fetch entirely
            console.log('[AuthContext] ✅ Profile is fresh, skipping fetch (cached)');
          }
        } else {
          // CRITICAL FIX: Initial authentication or first SIGNED_IN after login
          // DO NOT call async Supabase operations directly inside this callback - causes deadlock
          // Instead, trigger deferred resolution via state change (picked up by useEffect)
          console.log('[AuthContext] 🔐 Initial authentication detected in callback');
          console.log('[AuthContext] Setting loading state and scheduling DEFERRED profile resolution');
          setAuthStatus('loading');

          // Trigger deferred resolution OUTSIDE callback via useEffect
          setPendingAuthResolution(session.user);
          console.log('[AuthContext] ✅ Deferred resolution scheduled, exiting callback immediately');
        }
      } else {
        console.log('[AuthContext] No session user, clearing profile');
        setAuthStatus('unauthenticated');
        setResolvedProfile(null);
        lastProfileFetchRef.current = 0;
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const signOut = async () => {
    try {
      console.log('[AuthContext] ========== SIGN OUT STARTED ==========');

      const { error } = await supabase.auth.signOut();

      if (error) {
        console.error('[AuthContext] Sign out error:', error);
        throw error;
      }

      console.log('[AuthContext] Sign out successful, clearing state...');

      // Clear all auth state
      setUser(null);
      setSession(null);
      setAuthStatus('unauthenticated');
      setResolvedProfile(null);
      lastProfileFetchRef.current = 0;
      isInitialAuth.current = true;

      console.log('[AuthContext] State cleared, navigating to login...');
      navigate(ROUTES.LOGIN);

      console.log('[AuthContext] ========== SIGN OUT COMPLETE ==========');
    } catch (error) {
      console.error('[AuthContext] Sign out failed:', error);
      // Still try to clear state and navigate even if signOut fails
      setUser(null);
      setSession(null);
      setAuthStatus('unauthenticated');
      setResolvedProfile(null);
      lastProfileFetchRef.current = 0;
      isInitialAuth.current = true;
      navigate(ROUTES.LOGIN);
    }
  };

  const refreshProfile = async () => {
    if (user) {
      console.log('[AuthContext] Refreshing profile for user:', user.id);
      await resolveAuthenticatedUser(user);
    }
  };

  // Map new authStatus to deprecated profileResolutionState for backward compatibility
  const profileResolutionState: 'bootstrapping' | 'resolving' | 'resolved' =
    authStatus === 'loading' ? 'resolving' : 'resolved';

  return (
    <AuthContext.Provider
      value={{
        user,
        userProfile: resolvedProfile, // CRITICAL: Use resolvedProfile from AuthContext state
        session,
        // Backward compatibility: map new states to old API
        loading: !isAuthReady, // Deprecated
        authBootstrapping: !isAuthReady, // Deprecated
        profileLoading: authStatus === 'loading', // Deprecated
        // New explicit state model
        isAuthReady,
        profileResolutionState, // Deprecated, mapped from authStatus
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
